/**
 * Client-side filtering for collection-grid-v2.
 *
 * data-filter-rise        – single value on parent, e.g. "Mid"
 * data-filter-color-sizes – per-colour size map: "black:XS,S,M;white:S,M,L"
 *
 * Rise      → multi-select checkbox   (hides/shows parent cards only)
 * Color     → single-select checkbox  (hides/shows + preselects swatch on card)
 * Size      → single-select checkbox  (hides/shows + preselects size on card)
 *
 * Color and size use plain checkboxes but enforce max-one-checked per group:
 * checking a new one automatically unchecks the previous selection, and
 * clicking an already-checked one simply unchecks it (standard checkbox behaviour).
 *
 * Colour+size filtering is child-level: "Blue + S" only shows cards that have
 * a Blue child with S available (in stock) on that same child.
 *
 * After filtering, visible cards have their active swatch and quick-buy size
 * updated using the existing grid-item.js helpers (syncSizeAcrossVariants,
 * swatch mouseenter simulation).
 */

class ClientSideFilters extends HTMLElement {
  constructor() {
    super();
    this.form       = null;
    this._section   = null;
    this._rise      = new Set();
    this._color     = null;
    this._size      = null;
    this._bubble      = null;
    this._activeFacets = null;
    this._updating    = false; // guard against cascaded change events
  }

  connectedCallback() {
    this.form = this.querySelector('.filters__form');
    if (!this.form) return;

    this._section = this.closest('.collection-group');

    const sectionId = this.getAttribute('data-section-id');
    this._bubble = sectionId
      ? document.getElementById(`filter-count-client-side-${sectionId}`)
      : null;
    this._activeFacets = sectionId
      ? document.getElementById(`active-facets-client-side-${sectionId}`)
      : null;

    this._bindEvents();
  }

  /**
   * Event binding
   */

  _bindEvents() {
    this.form.addEventListener('change', (e) => {
      if (this._updating) return;

      const input = e.target;
      if (input.type !== 'checkbox') return;
      const param = input.getAttribute('data-client-filter-param');
      if (!param) return;

      if (param === 'rise') {
        if (input.checked) {
          this._rise.add(input.value);
        } else {
          this._rise.delete(input.value);
        }

      } else if (param === 'color') {
        if (input.checked) {
          this._updating = true;
          this.form.querySelectorAll('input[data-client-filter-param="color"]').forEach(cb => {
            if (cb !== input) cb.checked = false;
          });
          this._updating = false;
          this._color = input.value;
        } else {
          this._color = null;
        }

      } else if (param === 'size') {
        if (input.checked) {
          this._updating = true;
          this.form.querySelectorAll('input[data-client-filter-param="size"]').forEach(cb => {
            if (cb !== input) cb.checked = false;
          });
          this._updating = false;
          this._size = input.value;
        } else {
          this._size = null;
        }
      }

      this._run();
    });
  }

  /**
   * Orchestration
   */

  _run() {
    this._applyFilters();
    this._preselectCards();
    this._updateBubble();
    this._renderActiveFacets();
    this._updateFilterAvailability();
  }

  /**
   * Filtering — show/hide .shop-grid-item-v2.shop-grid-item-v5
   */

  _applyFilters() {
    const root = this._section || document;
    const hasActiveFilters = this._rise.size > 0 || this._color !== null || this._size !== null;

    root.querySelectorAll('.editorial-tile').forEach(tile => {
      if (hasActiveFilters) {
        tile.setAttribute('hidden', '');
      } else {
        tile.removeAttribute('hidden');
      }
    });

    const gridItems = root.querySelectorAll('.shop-grid-item-v2.shop-grid-item-v5');

    gridItems.forEach(item => {
      let visible = true;

      // Rise — multi-select, parent-level
      if (this._rise.size > 0) {
        const itemRise = (item.getAttribute('data-filter-rise') || '').trim();
        visible = this._rise.has(itemRise);
      }

      // Color + Size — child-level combined
      if (visible && (this._color !== null || this._size !== null)) {
        const colorSizeMap = this._parseColorSizes(item);

        if (this._color !== null && this._size !== null) {
          const handle = this._toHandle(this._color);
          const sizesForColor = colorSizeMap.get(handle);
          visible = !!sizesForColor && sizesForColor.has(this._size);
        } else if (this._color !== null) {
          visible = colorSizeMap.has(this._toHandle(this._color));
        } else {
          // Size only — any child with this size
          visible = [...colorSizeMap.values()].some(sizes => sizes.has(this._size));
        }
      }

      // .shop-grid-large-image is the previous sibling
      const prev = item.previousElementSibling;
      const hasLargeImage = prev && prev.classList.contains('shop-grid-large-image');

      const next = item.nextElementSibling;
      const mobileInfo = next && next.classList.contains('shop-grid-item-bottom') && next.classList.contains('shop-grid-item-v5')
        ? next
        : null;

      if (visible) {
        item.removeAttribute('hidden');
        if (hasLargeImage) prev.removeAttribute('hidden');
        if (mobileInfo) mobileInfo.removeAttribute('hidden');
      } else {
        item.setAttribute('hidden', '');
        if (hasLargeImage) prev.setAttribute('hidden', '');
        if (mobileInfo) mobileInfo.setAttribute('hidden', '');
      }
    });
  }

  /**
   * Card preselection — activate the right swatch + size on visible cards
   * Always resolves to a concrete colour and size (defaults when no filter set)
   */

  _preselectCards() {
    const root = this._section || document;
    const gridItems = root.querySelectorAll('.shop-grid-item-v2.shop-grid-item-v5');

    gridItems.forEach(item => {
      if (item.hasAttribute('hidden')) return;

      // Resolve which colour to activate:
      // - filter selected → use that colour
      // - no filter       → use the first swatch on the card
      const colorToActivate = this._color !== null
        ? this._color
        : this._getFirstSwatchColorName(item);

      // Activate colour first — this switches the visible quick-buy item
      if (colorToActivate) {
        this._activateSwatchOnCard(item, colorToActivate);
      }

      // Resolve which size to activate:
      // - filter selected → use that size
      // - no filter       → use the first available size on the (now-active) quick-buy item
      // Run after a microtask so newQuickBuySwatch's setTimeout(0) has fired
      const size = this._size;
      setTimeout(() => {
        const sizeToActivate = size !== null
          ? size
          : this._getFirstAvailableSize(item);

        if (sizeToActivate) {
          this._activateSizeOnCard(item, sizeToActivate);
        }
      }, 0);
    });
  }

  /**
   * Return the colour name (raw, not handleized) of the first .swatch-wrap
   * on a card, read from data-swatch-color-label on the anchor.
   */
  _getFirstSwatchColorName(item) {
    const $firstAnchor = $(item).find('.swatch-wrap a[data-swatch-color-label]').first();
    if (!$firstAnchor.length) return null;
    return $firstAnchor.attr('data-swatch-color-label');
  }

  /**
   * Return the label of the first available size in the currently-visible
   * quick-buy item on a card.
   */
  _getFirstAvailableSize(item) {
    const $visibleQuickBuy = $(item).find('.shop-grid-item__quick-buy-item:not([hidden])');
    if (!$visibleQuickBuy.length) return null;

    let firstSize = null;
    $visibleQuickBuy.find('.size-button').each(function() {
      const $radio = $(this).find('.size-radio');
      if (!$radio.hasClass('disabled') && !$radio.is(':disabled')) {
        firstSize = $(this).find('.radio-button-label').text().trim();
        return false; // break
      }
    });
    return firstSize;
  }

  /**
   * Activate a colour swatch on a card element (desktop or mobile).
   * Triggers swatchHover + newQuickBuySwatch and marks .active.
   */
  _activateSwatchOnElement($el, colorHandle) {
    const $targetAnchor = $el.find(`.swatch-wrap a[data-swatch-color-label="${colorHandle}"]`);
    if (!$targetAnchor.length) return;

    const $targetWrap = $targetAnchor.closest('.swatch-wrap');

    $targetAnchor.trigger('mouseenter.swatchHover');
    $targetWrap.trigger('mouseenter.newQuickBuySwatch');

    $el.find('.swatch-wrap').removeClass('active');
    $targetWrap.addClass('active');
  }

  /**
   * Activate a colour swatch on both the desktop card and the paired mobile
   * .shop-grid-item-bottom element.
   *
   * Accepts either a raw colour name ("Midnight Blue") or a handle
   * ("midnight-blue") — both are normalised via _toHandle before lookup.
   */
  _activateSwatchOnCard(item, colorName) {
    const colorHandle = this._toHandle(colorName);
    const $item = $(item);

    // Desktop card
    this._activateSwatchOnElement($item, colorHandle);

    // Mobile bottom bar — direct next sibling of the card element
    const next = item.nextElementSibling;
    if (next && next.classList.contains('shop-grid-item-bottom') && next.classList.contains('shop-grid-item-v5')) {
      this._activateSwatchOnElement($(next), colorHandle);
    }
  }

  /**
   * Activate a size on a card.
   * Updates the visible quick-buy item directly (correct variant ID), then
   * syncs across all other colour variants via grid-item.js's helper.
   */
  _activateSizeOnCard(item, size) {
    const $item = $(item);

    const $visibleQuickBuy = $item.find('.shop-grid-item__quick-buy-item:not([hidden])');
    if ($visibleQuickBuy.length) {
      const $form = $visibleQuickBuy.find('form');
      $form.find('.size-button').removeClass('active');
      $form.find('.size-button').each(function() {
        const label = $(this).find('.radio-button-label').text().trim();
        if (label === size) {
          const $radio = $(this).find('.size-radio');
          if (!$radio.hasClass('disabled') && !$radio.is(':disabled')) {
            $(this).addClass('active');
            $radio.prop('checked', true);
            $form.find('.quick-buy-variant-id').val($radio.val());
          }
          return false; // break
        }
      });
    }

    if (typeof syncSizeAcrossVariants === 'function') {
      syncSizeAcrossVariants($item, size);
    }
  }

  /**
   * Helpers
  */

  _toHandle(str) {
    return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  _parseColorSizes(el) {
    const raw = (el.getAttribute('data-filter-color-sizes') || '').trim();
    const map = new Map();
    if (!raw) return map;

    raw.split(';').forEach(entry => {
      const colonIdx = entry.indexOf(':');
      if (colonIdx === -1) return;
      const colorHandle = entry.slice(0, colonIdx).trim();
      const sizes = entry.slice(colonIdx + 1).split(',').map(s => s.trim()).filter(Boolean);
      if (colorHandle && sizes.length) {
        map.set(colorHandle, new Set(sizes));
      }
    });

    return map;
  }

  /**
   * Active facets bar
   */
  _renderActiveFacets() {
    if (!this._activeFacets) return;

    const pills = [];

    this._rise.forEach(value => {
      pills.push({ label: value, param: 'rise', value });
    });

    if (this._color !== null) {
      pills.push({ label: this._color, param: 'color', value: this._color });
    }

    if (this._size !== null) {
      pills.push({ label: this._size, param: 'size', value: this._size });
    }

    if (pills.length === 0) {
      this._activeFacets.setAttribute('hidden', '');
      this._activeFacets.innerHTML = '';
      return;
    }

    const svgX = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1.32422 18.8389L19.0019 1.1612" stroke="#6487BA" stroke-width="0.8" stroke-linecap="square"/>
      <path d="M1.32422 0.838867L19.0019 18.5165" stroke="#6487BA" stroke-width="0.8" stroke-linecap="square"/>
    </svg>`;

    const totalCount = pills.length;
    let inner = `<a href="#" class="active-facet__clear-all js-client-clear-all">
      <span class="link" aria-label="Clear ${totalCount} ${totalCount === 1 ? 'filter' : 'filters'}"> Clear All</span>
    </a>`;

    pills.forEach(({ label, param, value }) => {
      inner += `<div class="active-facet__remove">
        <a href="#" aria-label="Remove ${label}" data-client-remove-param="${param}" data-client-remove-value="${value}">
          ${svgX}
        </a>
        ${label}
      </div>`;
    });

    this._activeFacets.innerHTML = `<div class="active-facets">${inner}</div>`;
    this._activeFacets.removeAttribute('hidden');

    // Clear All
    this._activeFacets.querySelector('.js-client-clear-all').addEventListener('click', (e) => {
      e.preventDefault();
      this._rise.clear();
      this._color = null;
      this._size  = null;
      this.form.querySelectorAll('input[type="checkbox"]').forEach(cb => { cb.checked = false; });
      this._run();
    });

    // Individual remove links
    this._activeFacets.querySelectorAll('a[data-client-remove-param]').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const param = link.getAttribute('data-client-remove-param');
        const value = link.getAttribute('data-client-remove-value');

        if (param === 'rise') {
          this._rise.delete(value);
          const cb = this.form.querySelector(`input[data-client-filter-param="rise"][value="${value}"]`);
          if (cb) cb.checked = false;
        } else if (param === 'color') {
          this._color = null;
          this.form.querySelectorAll('input[data-client-filter-param="color"]').forEach(cb => { cb.checked = false; });
        } else if (param === 'size') {
          this._size = null;
          this.form.querySelectorAll('input[data-client-filter-param="size"]').forEach(cb => { cb.checked = false; });
        }

        this._run();
      });
    });
  }

  /**
   * Filter availability — disable options that would yield zero results
   * given the *other* currently-active filters.
   */

  _updateFilterAvailability() {
    const root = this._section || document;
    const allItems = [...root.querySelectorAll('.shop-grid-item-v2.shop-grid-item-v5')];

    // Size filters
    const sizeInputs = this.form.querySelectorAll('input[data-client-filter-param="size"]');
    sizeInputs.forEach(input => {
      const candidateSize = input.value;
      const available = allItems.some(item => {
        // Must pass rise filter (if active)
        if (this._rise.size > 0) {
          const itemRise = (item.getAttribute('data-filter-rise') || '').trim();
          if (!this._rise.has(itemRise)) return false;
        }
        // Must pass color+size combined
        const colorSizeMap = this._parseColorSizes(item);
        if (this._color !== null) {
          const sizesForColor = colorSizeMap.get(this._toHandle(this._color));
          return !!sizesForColor && sizesForColor.has(candidateSize);
        }
        // No color selected — any color that has this size
        return [...colorSizeMap.values()].some(sizes => sizes.has(candidateSize));
      });

      if (available) {
        input.removeAttribute('disabled');
      } else {
        input.setAttribute('disabled', '');
        if (input.checked) { input.checked = false; this._size = null; }
      }
    });

    // Color filters
    const colorInputs = this.form.querySelectorAll('input[data-client-filter-param="color"]');
    colorInputs.forEach(input => {
      const candidateHandle = this._toHandle(input.value);
      const available = allItems.some(item => {
        // Must pass rise filter (if active)
        if (this._rise.size > 0) {
          const itemRise = (item.getAttribute('data-filter-rise') || '').trim();
          if (!this._rise.has(itemRise)) return false;
        }
        // Must have this color, and if a size is selected it must be available
        const colorSizeMap = this._parseColorSizes(item);
        const sizesForColor = colorSizeMap.get(candidateHandle);
        if (!sizesForColor) return false;
        if (this._size !== null) return sizesForColor.has(this._size);
        return true;
      });

      if (available) {
        input.removeAttribute('disabled');
      } else {
        input.setAttribute('disabled', '');
        if (input.checked) { input.checked = false; this._color = null; }
      }
    });

    // Rise filters
    const riseInputs = this.form.querySelectorAll('input[data-client-filter-param="rise"]');
    riseInputs.forEach(input => {
      const candidateRise = input.value;
      const available = allItems.some(item => {
        const itemRise = (item.getAttribute('data-filter-rise') || '').trim();
        if (itemRise !== candidateRise) return false;
        // Must also satisfy color+size constraints
        if (this._color === null && this._size === null) return true;
        const colorSizeMap = this._parseColorSizes(item);
        if (this._color !== null && this._size !== null) {
          const sizesForColor = colorSizeMap.get(this._toHandle(this._color));
          return !!sizesForColor && sizesForColor.has(this._size);
        }
        if (this._color !== null) {
          return colorSizeMap.has(this._toHandle(this._color));
        }
        // Size only
        return [...colorSizeMap.values()].some(sizes => sizes.has(this._size));
      });

      if (available) {
        input.removeAttribute('disabled');
      } else {
        input.setAttribute('disabled', '');
        if (input.checked) { input.checked = false; this._rise.delete(candidateRise); }
      }
    });
  }

  /**
   * Bubble
   */

  _updateBubble() {
    if (!this._bubble) return;

    const total = this._rise.size
      + (this._color !== null ? 1 : 0)
      + (this._size  !== null ? 1 : 0);

    if (total > 0) {
      this._bubble.removeAttribute('hidden');
      const sr = this._bubble.querySelector('.sr-only');
      if (sr) sr.textContent = `${total} ${total === 1 ? 'filter' : 'filters'} active`;
    } else {
      this._bubble.setAttribute('hidden', '');
    }
  }
}

if (!customElements.get('client-side-filters')) {
  customElements.define('client-side-filters', ClientSideFilters);
}
